<template>
    <div>
        <div v-show="createNodeVisible">
            <div class="d-flex align-items-center mb-2">
                <div class="flex-grow-1 new-screen-input">
                    <input
                        :id="id + 'new-node-title'"
                        class="form-control"
                        type="text"
                        placeholder="Enter a title for the new screen"
                        v-model="newNodeTitle"
                        v-on:input="formatCreatedNode($event.target.value)"
                    />
                </div>
                <div class="flex-grow-0">
                    <span class="badge badge-info badge-tiny align-text-bottom ml-2 mr-1">New</span>
                    <span
                        class="icon icon-glyphicons-basic-times icon-btn p-1 text-secondary align-middle"
                        @click="cancelCreateNode()"
                    ></span>
                </div>
            </div>
        </div>
        <div v-show="!createNodeVisible">
            <v-select
                ref="nodeSelector"
                v-model="selectedNode"
                :multiple="false"
                :options="nodeOptions"
                :close-on-select="true"
                label="label"
                class="mw-100 mb-2"
                :class="{'vs-invalid': isInvalid && !disabled}"
                :placeholder="placeholder"
                :disabled="disabled"
                :taggable="true"
                :create-option="createNodeOption"
                v-on:input="emitUpdate(selectedNode)"
            >
                <template #list-header="{ filteredOptions, search }">
                    <li v-show="!Boolean(search)" @click="showCreateNode()">
                        <div class="px-3 my-1 py-1 pointer new-screen-option">
                            <span class="h5 icon-mr">+</span>Create a new screen
                        </div>
                        <div v-show="filteredOptions.length > 0" class="px-3 pb-1">
                            <span class="text-secondary header-sm">ALL SCREENS</span>
                        </div>
                    </li>
                </template>
                <template slot="option" slot-scope="option">
                    <div>
                        <span v-show="option.id" class="node-dropdown-label">{{ option.label }}</span>
                        <span v-show="!option.id" class="node-dropdown-label pt-2 pb-2">
                            <span class="h5 icon-mr">+</span> Create
                            <span class="font-weight-bolder">{{ option.title }}</span>
                        </span>
                    </div>
                </template>
                <template v-slot:selected-option="option">
                    <span v-show="option.id">{{ option.label }}</span>
                    <span v-show="!option.id">
                        <span class="mr-2">{{ option.title }}</span>
                        <span class="badge badge-info badge-tiny align-text-bottom">New</span>
                    </span>
                </template>
                <template slot="no-options">
                    <span></span>
                </template>
            </v-select>
        </div>
    </div>
</template>

<script>
export default {
    name: "NodeSelect",
    props: {
        value: {
            type: [Object, Number],
            required: false,
        },
        nodeOptions: {
            type: Array,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
            required: false,
        },
        placeholder: {
            type: String,
            required: false,
            default() {
                return "Select or create a new screen"
            },
        },
    },
    data() {
        return {
            createNodeVisible: false,
            newNodeTitle: "",
            selectedNode: this.value,
            id: null,
            isInvalid: false,
        }
    },
    computed: {
        selectedNodeParsed() {
            if (typeof this.value === "object") {
                return this.value
            } else {
                // An ID was probably passed in, so select the associated node from the list.
                return this.nodeOptions.find((n) => n.id === this.value)
            }
        },
    },
    watch: {
        // Watch for changes to the selected node in the parent element.
        selectedNodeParsed: function (value) {
            this.selectedNode = this.selectedNodeParsed
            this.isInvalid = false
        },
    },
    methods: {
        // Create a node option - used to format new node options as a user free types
        // in the selector.
        createNodeOption(newOption) {
            return {
                title: newOption,
                label: newOption,
            }
        },
        // Show the create node input field instead of the selector dropdown with
        // all the options.
        showCreateNode() {
            // Show the create node input.
            this.createNodeVisible = true
            // Bring the input into focus.
            this.$nextTick(() => {
                document.getElementById(this.id + "new-node-title").focus()
            })
        },
        // Cancel out of the create node input, and show the selector dropdown again.
        cancelCreateNode() {
            this.createNodeVisible = false
            // Bring the input into focus.
            this.$nextTick(() => {
                this.$refs.nodeSelector.$refs.search.focus()
            })
        },
        emitUpdate(value) {
            this.$emit("input", value)
        },
        // Format a node created using the create-new input field (not the selector).
        formatCreatedNode(value) {
            let createdNode = {
                id: null,
                label: null,
                title: this.newNodeTitle,
            }
            this.$emit("input", createdNode)
        },
        reset() {
            this.createNodeVisible = false
            this.newNodeTitle = ""
            this.isInvalid = false
        },
        // Called by the parent component to check if a node is selected.
        validate() {
            if (this.value === null) {
                this.isInvalid = true
                return false
            }
            return true
        },
    },
    created() {
        this.id = this._uid
    },
    mounted() {
        this.selectedNode = this.selectedNodeParsed
    },
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/variables.scss";

.node-dropdown-label {
    font-size: 15px;
}

.header-sm {
    font-weight: 600;
    font-size: 13px;
}

.new-screen-option {
    &:hover {
        background-color: $lightest;
    }
}

.new-screen-input {
    margin-right: 2px;
}
</style>

<style lang="scss">
@import "@/assets/styles/variables.scss";

.vs-invalid {
    .vs__dropdown-toggle {
        border-color: $danger;
    }
}
</style>